import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalContext } from '../../contexts/ModalContext'
import i18n from '../../i18n'
import { useBadges } from '../../pages/saving/hooks/useBadges'
import ModalWrapper from '../../wrappers/modalWrapper/ModalWrapper'
import BadgeSwiper from './components/BadgeSwiper'
import { TierData } from './types/tierData'

interface CategoryData {
	tiers: TierData[]
	categoryName: string
}

const Badges = () => {
	const badgeHook = useBadges()
	const { insertModal } = useContext(ModalContext)
	const { t } = useTranslation('modals_badges')

	const swiperData = useMemo(() => {
		const badges = badgeHook.badges
		const userBadges = badgeHook.userBadges

		const categories: Record<string, CategoryData> = {}

		badges?.forEach(badge => {
			if (!categories[badge.category]) {
				categories[badge.category] = {
					tiers: [],
					categoryName: i18n.language === 'en' ? badge.nameEn : badge.nameFi,
				}
			}

			badge.tiers.forEach(tier => {
				const userBadge = userBadges?.find(ub => ub.badgeId === badge.id && ub.tierId === tier.id)

				categories[badge.category].tiers.push({
					badge,
					tier,
					isCompleted: !!userBadge,
					userBadge: userBadge || undefined,
				})
			})
		})

		Object.values(categories).forEach(categoryData => {
			categoryData.tiers = categoryData.tiers
				.filter(tierData => tierData.tier.tierLevel > 0)
				.sort((a, b) => a.tier.tierLevel - b.tier.tierLevel)
		})

		return categories
	}, [badgeHook.badges, badgeHook.userBadges])

	const handleSeeAll = (tiers: TierData[], categoryName: string) => {
		insertModal('badges-tiers', { tiers, categoryName })
	}

	return (
		<ModalWrapper>
			{badgeHook.isLoading ? (
				<div className='flex items-center justify-center h-full text-text-dark'>
					<span>Loading...</span>
				</div>
			) : (
				<>
					{Object.entries(swiperData).map(([category, { tiers, categoryName }]) => (
						<div key={category} className='mb-6'>
							<div className='flex justify-between items-center mb-3'>
								<h3 className='text-lg font-semibold capitalize'>{categoryName}</h3>

								<span
									className='text-center text-base text-[#127d27] font-semibold'
									onClick={() => handleSeeAll(tiers, categoryName)}
								>
									{t('seeAll')}
								</span>
							</div>
							<BadgeSwiper tiers={tiers} color='#f5f5f5' isLoading={badgeHook.isLoading} />
						</div>
					))}
				</>
			)}
		</ModalWrapper>
	)
}

export default Badges
